import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useState } from 'react';
import api from '../lib/api';

export const Route = createFileRoute('/client/processes/$processId/sync-history')({
  component: ClientProcessSyncHistoryPage,
});

type MovementModal = {
  movementDate?: string | null;
  description?: string | null;
  rawData?: unknown;
  movementId?: string | null;
  movementType?: string | null;
};

const MOVEMENTS_LIMIT = 10;

function MovementDetailModal({
  open,
  onClose,
  item,
}: {
  open: boolean;
  onClose: () => void;
  item: MovementModal | null;
}) {
  if (!open || !item || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-2 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto p-5"
        style={{
          fontFamily: 'Poppins, sans-serif',
          borderRadius: 15,
          boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="text-[16px] font-semibold text-[#06141B]">Movimentação</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-800 px-2"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <div className="space-y-3 text-sm text-[#06141B]">
          <p className="text-xs text-gray-500">
            {item.movementDate ? new Date(item.movementDate).toLocaleString() : ''}
            {item.movementId?.startsWith('manual-') ? (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px]">
                Manual
              </span>
            ) : null}
          </p>
          {item.movementType ? (
            <p className="text-xs text-gray-600">Tipo: {item.movementType}</p>
          ) : null}
          <p className="whitespace-pre-wrap">{item.description || '—'}</p>
          {item.rawData != null && item.rawData !== '' ? (
            <div>
              <p className="text-[11px] font-medium text-gray-600 mb-1">Dados brutos</p>
              <pre className="text-xs bg-gray-50 p-3 rounded-[10px] overflow-auto max-h-48 border border-gray-100">
                {typeof item.rawData === 'string'
                  ? item.rawData
                  : JSON.stringify(item.rawData, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full py-2.5 rounded-[12px] bg-[#06141B] text-white text-sm font-medium"
        >
          Fechar
        </button>
      </div>
    </div>,
    document.body,
  );
}

function ClientProcessSyncHistoryPage() {
  const { processId } = Route.useParams();
  const [movementPage, setMovementPage] = useState(1);
  const [modalItem, setModalItem] = useState<MovementModal | null>(null);

  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['client-portal', 'process', processId, 'sync-history', movementPage],
    queryFn: async () => {
      const r = await api.get(`/client-portal/processes/${processId}/sync-history`, {
        params: { page: movementPage, limit: MOVEMENTS_LIMIT },
      });
      return r.data?.data ?? r.data;
    },
  });

  const movements = data?.movements ?? [];
  const movementTotalPages = data?.totalPages ?? 1;
  const movementTotal = data?.total ?? movements.length;

  if (isLoading) {
    return (
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <p className="text-sm text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Carregando histórico...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <p className="text-sm text-red-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Erro ao carregar histórico.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <MovementDetailModal
        open={!!modalItem}
        onClose={() => setModalItem(null)}
        item={modalItem}
      />

      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <h2 className="text-[14px] font-semibold text-[#06141B]">Histórico de Movimentações</h2>
        <p className="text-[12px] text-gray-500 mt-1">
          Inclui publicações do diário e registros manuais. Clique para ver o conteúdo completo.
        </p>
        {!movements.length ? (
          <p className="text-sm text-gray-500 mt-4">Sem movimentações registradas.</p>
        ) : (
          <>
            <div className="mt-4 space-y-2">
              {movements.map((m: any) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() =>
                    setModalItem({
                      movementDate: m.movementDate,
                      description: m.description,
                      rawData: m.rawData,
                      movementId: m.movementId,
                      movementType: m.movementType,
                    })
                  }
                  className="w-full text-left p-4 rounded-[12px] border border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <p className="text-xs text-gray-500 flex flex-wrap items-center gap-2">
                    {m.movementDate ? new Date(m.movementDate).toLocaleString() : ''}
                    {m.movementId?.startsWith('manual-') ? (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px]">
                        Manual
                      </span>
                    ) : null}
                  </p>
                  <p className="text-sm text-[#06141B] mt-1 line-clamp-2 whitespace-pre-wrap">
                    {m.description}
                  </p>
                </button>
              ))}
            </div>
            {movementTotalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setMovementPage((p) => Math.max(1, p - 1))}
                  disabled={movementPage === 1}
                  className="px-4 py-2 rounded-lg disabled:opacity-50 text-sm bg-[#F9FAFB] border border-gray-200"
                >
                  &lt;
                </button>
                <span className="text-xs text-gray-700">
                  Página {movementPage} de {movementTotalPages} ({movementTotal}{' '}
                  {movementTotal === 1 ? 'item' : 'itens'})
                </span>
                <button
                  type="button"
                  onClick={() => setMovementPage((p) => Math.min(movementTotalPages, p + 1))}
                  disabled={movementPage === movementTotalPages}
                  className="px-4 py-2 rounded-lg disabled:opacity-50 text-sm bg-[#F9FAFB] border border-gray-200"
                >
                  &gt;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
